import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { VacancyCopy } from 'types/vacancyTypes';
import { validateSalary } from 'utils/salaryHelpers';
import { SaveRefused } from 'hooks/useAutoSave';
import { SCREEN_NAME, nextSendNo } from 'utils/screenName';

/** The fields the salary card owns. */
export const SALARY_FIELDS = [
  'salary_min',
  'salary_max',
  'currency',
  'pay_period',
  'salary_note',
] as const;

/**
 * Can this page's salary be stored as it stands?
 *
 * The question is only whether what is THERE can be stored, never whether it is
 * complete: an empty salary is unfinished work, which is kept without comment
 * (D7), and completeness is demanded at publish. The server refuses the WHOLE
 * page when the maximum is below the minimum, so a salary that cannot be stored
 * has to be left out or every other word on the screen is lost with it.
 */
export const salaryCanBeStored = (copy: VacancyCopy): boolean => {
  const rangeEnabled =
    copy.salary_min !== null &&
    copy.salary_min !== undefined &&
    copy.salary_max !== copy.salary_min;

  const result = validateSalary({
    salary_min: copy.salary_min,
    salary_max: copy.salary_max,
    currency: copy.currency,
    pay_period: copy.pay_period,
    salary_note: copy.salary_note,
    rangeEnabled,
    mandatory: false,
  });

  return Object.keys(result.errors).length === 0;
};

/**
 * What the corner says when the salary was left out of a save.
 *
 * Both sentences are approved by Steven. "numbers only" is 5b item 4, for an
 * amount that is not a number. For a maximum below the minimum it would be
 * untrue, so that case says what the salary block itself says under the box
 * (approved 3 Oct 2026).
 */
export const SALARY_NOT_SAVED_NUMBERS = 'salary not saved: numbers only';
export const SALARY_NOT_SAVED_RANGE =
  'salary not saved: maximum salary must be greater than minimum salary';

export const salaryNotSavedNote = (copy: VacancyCopy): string => {
  const { salary_min: min, salary_max: max } = copy;
  const maxBelowMin =
    typeof min === 'number' && typeof max === 'number' && max < min;

  return maxBelowMin ? SALARY_NOT_SAVED_RANGE : SALARY_NOT_SAVED_NUMBERS;
};

/**
 * What to send, and whether the salary had to be left out of it.
 */
export const jobPageRequest = (
  copy: VacancyCopy
): { payload: Record<string, unknown>; salaryLeftOut: boolean } => {
  const payload = jobPagePayload(copy);
  const storable = salaryCanBeStored(copy);
  if (!storable) SALARY_FIELDS.forEach((field) => delete payload[field]);
  return { payload, salaryLeftOut: !storable };
};

export const jobPagePath = (vacancyId: number): string =>
  `/v4/vacancies/${vacancyId}/copy`;

/**
 * The body the job-page endpoint expects. Identical to what the Save button
 * used to send (VacancyContext.saveHandler), kept in one place so the ordinary
 * save and the one sent while the tab closes cannot drift apart.
 */
export const jobPagePayload = (copy: VacancyCopy): Record<string, unknown> => {
  const rest = { ...copy } as Record<string, unknown>;
  delete rest.salary_metadata;

  return {
    ...rest,
    vac_text_uniqueness: JSON.stringify(copy.vac_text_uniqueness),
    vac_text_uniqueness_beta: copy.vac_text_uniqueness,
    pictures: {
      logo: copy.pictures?.logo?.id,
      image_cb1: copy.pictures?.image_cb1?.id,
      image_cb3: copy.pictures?.image_cb3?.id,
    },
  };
};

/**
 * A refusal the server will NEVER accept, whatever we do: it named the fields it
 * cannot store. Trying the same request again forever is pointless and leaves
 * the person staring at "Not saved, trying again" with no way out.
 */
export class JobPageRefusal extends Error {
  constructor(public readonly fields: string[]) {
    super('The job page was refused.');
    this.name = 'JobPageRefusal';
  }
}

/** Which count of the job page a save is built on (D10). */
export interface BasedOn {
  version: number | null;
  /** "Keep mine": store it although the count has moved on. */
  overwrite?: boolean;
}

/**
 * The body as it travels: the page, the count it was built on, and the name
 * of this open screen. Also used for the save sent while the tab goes away.
 */
export const withCount = (
  payload: Record<string, unknown>,
  basedOn?: BasedOn
): Record<string, unknown> => ({
  ...payload,
  screen: SCREEN_NAME,
  sent_no: nextSendNo(),
  ...(basedOn?.version != null ? { based_on: basedOn.version } : {}),
  ...(basedOn?.overwrite ? { overwrite: true } : {}),
});

/**
 * Stores the job page and returns the moment the SERVER says it kept the work.
 *
 * Deliberately not the app's shared fetch wrapper: that one raises a global
 * error toast for every failed answer, and auto-save retries by itself every
 * few seconds, so the screen would fill with toasts while the status line is
 * already saying "Not saved, trying again" (scene 12). Same reasoning as the
 * quiet retry the template previews already have.
 *
 * @throws when the server did not confirm the work
 */
export const saveJobPage = async (
  vacancyId: number,
  payload: Record<string, unknown>,
  signal?: AbortSignal,
  basedOn?: BasedOn
): Promise<{ keptAt: string; keptVersion: number | null }> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';

  const response = await fetch(`${base}${jobPagePath(vacancyId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      // 'preserve': the default mode DELETES an expired token as a side
      // effect, which turns the next attempt into "Bearer null" for ever.
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(withCount(payload, basedOn)),
    // Given up when the save took too long or the work was given up.
    signal,
  });

  /*
   * Being signed out is not a save problem and must not become an endless
   * retry: the app's shared request path sends the person to the login screen
   * for this, and this path has to do the same or the corner says "trying
   * again" for the rest of the session (apiHelpers.ts:63-66).
   */
  if (response.status === 401) {
    if (window.location.pathname !== '/') {
      leaveSignedOut();
    }
    throw new Error('The session has ended.');
  }

  // D10: the screen is out of date. Not a failure: the person chooses.
  if (response.status === 409) {
    const refused = (await response.json().catch(() => ({}))) as {
      out_of_date?: boolean;
      data?: {
        by?: string | null;
        own?: boolean;
        at?: string | null;
        kept_version?: number;
      };
    };
    if (refused.out_of_date) {
      throw new SaveRefused({
        by: refused.data?.by ?? null,
        own: refused.data?.own === true,
        at: refused.data?.at ?? null,
        keptVersion: Number(refused.data?.kept_version ?? 0),
      });
    }
  }

  if (response.status === 422) {
    const refused = (await response.json().catch(() => ({}))) as {
      errors?: Record<string, unknown>;
    };
    // "salary_min" and "pictures.logo" both name a field of the page.
    const fields = Object.keys(refused.errors ?? {}).map(
      (field) => field.split('.')[0]
    );
    throw new JobPageRefusal([...new Set(fields)]);
  }

  if (!response.ok) {
    throw new Error(`The job page was not stored (${response.status}).`);
  }

  const body = (await response.json()) as {
    success?: boolean;
    data?: { kept_at?: string; kept_version?: number };
  };

  // done-point 7: never report a save the server did not make.
  if (body.success === false) {
    throw new Error('The job page was not stored.');
  }

  return {
    keptAt: body.data?.kept_at ?? new Date().toISOString(),
    keptVersion:
      typeof body.data?.kept_version === 'number'
        ? body.data.kept_version
        : null,
  };
};
